/** Temporary request plan. Text statuses distinguish retrieved evidence, delivery and unfinished work. */
import { useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import type { ChatMessage } from '../../models/ChatMessage';
import type { ChatPlanStep } from '../../models/ChatPlan';

function statusLabel(step: ChatPlanStep): string {
  if (step.status === 'completed') return step.outcome === 'empty' ? 'No results' : step.outcome === 'partial' ? 'Partial evidence gathered' :
    step.kind === 'read' ? 'Evidence gathered' : step.kind === 'draft' ? 'Draft prepared' : 'Answer delivered';
  if (step.status === 'not_needed') return step.outcome === 'failed' ? 'Revised after failed read' :
    step.outcome === 'partial' ? 'Revised with partial evidence' : 'No longer needed';
  return ({ pending: 'Planned', in_progress: 'Working', waiting_input: 'Waiting for input', blocked: 'Unfinished', stopped: 'Stopped' })[step.status];
}
export default function ChatPlanMessage({ message }: { message: ChatMessage }) {
  const [expanded, setExpanded] = useState(true);
  const plan = message.plan;
  if (!plan) return null;
  const heading = message.eventData?.message || ({ running: 'Plan', waiting_input: 'Waiting for your input',
    completed: 'Plan complete', blocked: 'Plan has unfinished work', stopped: 'Plan stopped' })[plan.status];
  const contentId = `chat-plan-${plan.runId}`;
  return <section aria-label="Execution plan" className="my-2 min-w-0 max-w-full rounded border border-gray-200 px-3 py-2 text-xs dark:border-gray-700">
    <button type="button" aria-expanded={expanded} aria-controls={contentId}
      onClick={() => setExpanded(value => !value)}
      className="flex w-full items-center justify-between gap-2 rounded text-left font-medium text-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 dark:text-gray-200">
      <span className="min-w-0 break-words">{heading}</span>
      <ChevronDownIcon aria-hidden="true" className={`h-4 w-4 shrink-0 ${expanded ? '' : '-rotate-90'}`} />
    </button>
    <div id={contentId} hidden={!expanded}>
      <ol className="mt-2 space-y-2" aria-live="polite" aria-relevant="text">
        {plan.steps.map(step => <li key={step.id} className="flex min-w-0 gap-2 text-gray-700 dark:text-gray-200">
          <span aria-hidden="true" className="shrink-0">{step.status === 'completed' ? '✓' : step.status === 'in_progress' ? '●' : '○'}</span>
          <div className="min-w-0 break-words [overflow-wrap:anywhere]">
            <span>{step.label}</span><span className="ml-2 text-gray-500 dark:text-gray-400">{statusLabel(step)}</span>
          </div>
        </li>)}
      </ol>
    </div>
  </section>;
}
